import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Skeleton } from 'components/ui/skeleton';
import { isEmployeePriced } from 'pages/pricelists/modal/employeePricing';
import { useFindPricelistsByClientQuery } from 'store/api/pricelists';
import { ScopeType } from 'types/pricelists';
import { t } from 'utils/getTranslations';

export interface FollowedPricelist {
    id: number;
    name: string;
}

interface Props {
    currencyTypeCode?: string | null;
    selectedId: number | null;
    onPick: (model: FollowedPricelist) => void;
    disabled?: boolean;
}

/** The firm's employee-priced model lists in the contract's currency: the lists a contract's price list may follow. */
export function ModelPricelistOptions({ currencyTypeCode, selectedId, onPick, disabled }: Props): JSX.Element {
    const { data: pricelists, isLoading } = useFindPricelistsByClientQuery('model');
    const models = (pricelists ?? []).filter(
        (pricelist) =>
            isEmployeePriced(pricelist) &&
            (pricelist.scopeTypeCode === ScopeType.TENANT || pricelist.scopeTypeCode === ScopeType.COMPANY) &&
            (!currencyTypeCode || pricelist.currencyTypeCode === currencyTypeCode)
    );

    if (isLoading) {
        return <Skeleton className="h-5 w-48" />;
    }
    if (!models.length) {
        return <p className="text-muted-foreground text-sm">{t('pricelists.employee.no-models')}</p>;
    }
    return (
        <RadioGroup
            aria-label={t('contracts.pricing.model-lists')}
            value={selectedId == null ? null : String(selectedId)}
            onValueChange={(value) => {
                const model = models.find((pricelist) => String(pricelist.id) === value);
                if (model) {
                    onPick({ id: model.id, name: model.name });
                }
            }}
            disabled={disabled}
            className="gap-2"
        >
            {models.map((model) => (
                <Label key={model.id} className="cursor-pointer font-normal">
                    <RadioGroupItem value={String(model.id)} />
                    {model.name}
                </Label>
            ))}
        </RadioGroup>
    );
}
